import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'
import KeyboardView from '@components/view/KeyboardView'

import useI18n from '@i18n/useI18n'
import useDebounce from '@utils/useDebounce'

import { FormikHelpers, useFormik } from 'formik'

interface Values {
  firstname: string
  name: string
  mail: string
}

const Info = ({ form, onForm }: SignatureFormProps) => {
  const init: Values = React.useMemo(
    () => ({
      name: form.name || '',
      firstname: form.firstname || '',
      mail: form.mail || '',
    }),
    [form.name, form.firstname, form.mail]
  )

  const i18n = useI18n()

  const [firstName, debouncedFirstName, setFirstName] = useDebounce(init.firstname)
  const [name, debouncedName, setName] = useDebounce(init.name)
  const [mail, debouncedMail, setMail] = useDebounce(init.mail)

  React.useEffect(() => {
    setFieldValue('firstname', debouncedFirstName)
  }, [debouncedFirstName])

  React.useEffect(() => {
    setFieldValue('name', debouncedName)
  }, [debouncedName])

  React.useEffect(() => {
    setFieldValue('mail', debouncedMail)
  }, [debouncedMail])

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  return (
    <KeyboardView>
      <InputContainer>
        <Title>{i18n.t('screens.edlSignature.contactInformations.firstname')}</Title>
        <TextInput
          value={firstName}
          onChange={(value) => setFirstName(value)}
          placeholder={i18n.t(`screens.edlSignature.contactInformations.firstnamePlaceholder`)}
          numberOfLines={1}
        />
      </InputContainer>

      <InputContainer>
        <Title>{i18n.t('screens.edlSignature.contactInformations.name')}</Title>
        <TextInput
          value={name}
          onChange={(value) => setName(value)}
          placeholder={i18n.t(`screens.edlSignature.contactInformations.namePlaceholder`)}
          numberOfLines={1}
        />
      </InputContainer>

      <InputContainer>
        <Title>{i18n.t('screens.edlSignature.contactInformations.mail')}</Title>
        <TextInput
          value={mail}
          onChange={(value) => setMail(value)}
          placeholder={i18n.t(`screens.edlSignature.contactInformations.mailPlaceholder`)}
          numberOfLines={1}
        />
      </InputContainer>
    </KeyboardView>
  )
}

export default Info

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 8px;
`

const InputContainer = styled(View)`
  margin-bottom: 32px;
`
